/**
 * Format money with currency code (e.g., "USD 16.38")
 * @param {number} amount - The amount to format
 * @param {string} currency - The currency code (e.g., "USD", "COP")
 * @param {boolean} omitCurrency - If true, omit the currency prefix
 * @returns {string} Formatted money string
 */
export function formatMoney(amount, currency, omitCurrency = false) {
  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  if (omitCurrency) {
    return formatter.format(amount);
  }
  return `${currency} ${formatter.format(amount)}`;
}

/**
 * A movement's amount as a phone row prints it: `{ text, tone }`.
 *
 * The home currency drops its code and a zero fraction (`−18,000`, `+11`); a
 * foreign currency keeps both (`−USD 8.00`), since the code is what tells the
 * two apart. The sign comes from the type, and a negative stored amount (a
 * refund) flips it. A transfer between the user's own accounts is neither in
 * nor out, so it is unsigned and toned apart.
 *
 * @param {object} movement - needs `type`, `amount`, `currency`
 * @param {string} homeCurrency - the code shown without a prefix
 * @returns {{ text: string, tone: 'income'|'expense'|'transfer' }}
 */
export function formatRowAmount(movement, homeCurrency) {
  const amount = Number(movement?.amount) || 0;
  const abs = Math.abs(amount);
  const currency = movement?.currency;

  let number;
  if (currency && currency !== homeCurrency) {
    number = formatMoney(abs, currency);
  } else {
    const whole = Math.round(abs * 100) % 100 === 0;
    number = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    }).format(abs);
  }

  if (movement?.type === 'Transfer') return { text: number, tone: 'transfer' };

  const isIncome = (movement?.type === 'Income') !== (amount < 0);
  return {
    text: `${isIncome ? '+' : '−'}${number}`,
    tone: isIncome ? 'income' : 'expense',
  };
}

/**
 * A phone figure: whole amounts drop the fraction ("4,812,300"), others keep
 * two decimals ("8.50"). Unsigned; the caller places the sign and the code.
 */
export function formatFigure(amount) {
  const abs = Math.abs(Number(amount) || 0);
  const whole = Math.round(abs * 100) % 100 === 0;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(abs);
}

/** "COP 4,812,300", "−COP 612,300", and with `signed`, "+COP 6,200,011". */
export function formatPhoneMoney(currency, amount, { signed = false } = {}) {
  const value = Number(amount) || 0;
  const sign = value < 0 ? '−' : (signed && value > 0 ? '+' : '');
  return `${sign}${currency ? `${currency} ` : ''}${formatFigure(value)}`;
}

/**
 * A phone balance or total: the cents go from four digits up, where they are
 * noise and would push a line past the screen's width ("COP 4,727,537",
 * "USD 986.12"). Home's figures follow the same rule.
 */
export function formatPhoneBalance(currency, amount) {
  const value = Number(amount) || 0;
  return formatPhoneMoney(currency, Math.abs(value) >= 1000 ? Math.round(value) : value);
}
